import { useId, useRef } from 'react';
import { Upload } from 'lucide-react';

import { Button } from '@/components/Button/Button';

import styles from './FilePicker.module.css';

export const AI_UPLOAD_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

export const AI_UPLOAD_ACCEPTED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
] as const;

export const AI_UPLOAD_ACCEPT = [
  ...AI_UPLOAD_ACCEPTED_TYPES,
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.webp',
  '.pdf',
  '.docx',
].join(',');

export function validateAIUploadFile(file: File): string | null {
  if (!AI_UPLOAD_ACCEPTED_TYPES.includes(file.type as typeof AI_UPLOAD_ACCEPTED_TYPES[number])) {
    return 'Choose an image, PDF, or DOCX file.';
  }

  if (file.size > AI_UPLOAD_MAX_FILE_SIZE_BYTES) {
    return 'Files must be 10MB or smaller.';
  }

  return null;
}

interface FilePickerProps {
  disabled?: boolean;
  error?: string | null;
  onSelectFile: (file: File) => void;
}

export function FilePicker({
  disabled = false,
  error,
  onSelectFile,
}: FilePickerProps) {
  const inputId = useId();
  const helpId = useId();
  const errorId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);

  return (
    <div className={styles.dropzone}>
      <input
        accept={AI_UPLOAD_ACCEPT}
        aria-describedby={error ? `${helpId} ${errorId}` : helpId}
        className={styles.input}
        disabled={disabled}
        id={inputId}
        onChange={(event) => {
          const [file] = Array.from(event.target.files ?? []);
          if (file) {
            onSelectFile(file);
          }
          event.currentTarget.value = '';
        }}
        ref={inputRef}
        type="file"
      />
      <Upload aria-hidden="true" className={styles.icon} size={28} strokeWidth={2} />
      <p className={styles.title}>Upload your work</p>
      <p className={styles.help} id={helpId}>
        Standard image files, PDF, and DOCX formats are supported.
      </p>
      {error ? (
        <p className={styles.error} id={errorId} role="alert">
          {error}
        </p>
      ) : null}
      <Button
        disabled={disabled}
        onClick={() => {
          inputRef.current?.click();
        }}
        variant="secondary"
      >
        Choose File
      </Button>
    </div>
  );
}
